import type { AccessQueueType, ItEmailAction } from '@accounts/shared';

import { STR } from '../../../../i18n';

/**
 * SET UP IS GREEN, REMOVE IS RED (spec row 15; FINAL demo change 1). Every surface of the IT
 * step — the picker, the drafted email, the IT emails page and the Waiting on IT lane — draws
 * an item in one of two tones: the EXISTING success and danger text colours plus a 3px edge
 * bar. No new colour token. The words always say Set up / Remove beside the colour, so the
 * colour is never the only signal.
 */
export type ItTone = 'setup' | 'remove';

export const IT_TONE: Readonly<Record<ItTone, { word: string; className: string; bar: string }>> = {
  setup: { word: STR.itEmails.setUp, className: 'tx-success-strong', bar: 'var(--bg-success)' },
  remove: { word: STR.itEmails.remove, className: 'tx-danger-strong', bar: 'var(--bg-danger)' },
};

/** The inset edge bar, as a box shadow so it never shifts the row's layout. */
export function toneBar(tone: ItTone | null): string | undefined {
  return tone === null ? undefined : `inset 3px 0 0 ${IT_TONE[tone].bar}`;
}

/** An IT email item's tone: the server says Set up or Remove; this only colours it. */
export function toneOfAction(action: ItEmailAction): ItTone {
  return action === 'set_up' ? 'setup' : 'remove';
}

/** Which tone a Queue item's IT work is. A request is not IT work yet: no tone. */
export function toneOfQueueType(type: AccessQueueType): ItTone | null {
  if (type === 'needs_access' || type === 'requested') return 'setup';
  if (type === 'remove_access') return 'remove';
  return null;
}

export interface ToneLine {
  text: string;
  tone: ItTone | null;
}

const BULLET = /^\s*•/;
/** A checklist email's line ("- Remove the account …"): a bullet under its section. */
const DASH = /^\s*- /;
/** A checklist email's section heading: `SET UP.` or `REMOVE.` alone on its line. */
const SECTION = /^(SET UP|REMOVE)\.\s*$/;
const SET_UP_HEADING = /^SET UP\b/;
const REMOVE_HEADING = /^(REMOVE|ROTATE)\b/;
/** Any other all-caps heading ("ONBOARDING (Lena Okafor):") ends a coloured block. */
const OTHER_HEADING = /^[A-Z][A-Z ’']+[(:]/;

/**
 * Colours an IT email's plain text line by line, as the FINAL demo does. A SET UP / REMOVE
 * (or ROTATE) heading colours itself and the bullets under it until the next blank line or
 * the next heading; a bullet that names `SET UP.` or `REMOVE.` itself wins over its heading.
 * Everything else stays uncoloured. The text is never changed — only each line's tone —
 * so what is copied is exactly what was stored.
 *
 * A checklist's email (the onboarding / offboarding Draft IT email) is laid out as a bare
 * `SET UP.` / `REMOVE.` section line, then per system a label line and its `- ` lines, a
 * blank line between systems. Its section therefore holds across blank lines and colours
 * only the `- ` lines; the system labels and the closing lines stay plain.
 */
export function toneLines(text: string): ToneLine[] {
  let mode: ItTone | null = null;
  let section: ItTone | null = null;
  return text.split('\n').map((line) => {
    const bullet = BULLET.test(line);
    const dash = DASH.test(line);
    let tone: ItTone | null = null;
    if (line.trim() === '') {
      mode = null;
    } else if (dash) {
      tone = section;
    } else if (!bullet && SECTION.test(line)) {
      section = line.startsWith('SET UP') ? 'setup' : 'remove';
      mode = null;
      tone = section;
    } else if (!bullet && SET_UP_HEADING.test(line)) {
      section = null;
      mode = 'setup';
      tone = mode;
    } else if (!bullet && REMOVE_HEADING.test(line)) {
      section = null;
      mode = 'remove';
      tone = mode;
    } else if (!bullet && OTHER_HEADING.test(line)) {
      section = null;
      mode = null;
    } else if (bullet) {
      tone = line.includes('SET UP.') ? 'setup' : line.includes('REMOVE.') ? 'remove' : mode;
    }
    return { text: line, tone };
  });
}
